import { BaseService, Body, DELETE, ExtraMap, GET, Header, Path, POST, Query, QueryMap, Signal } from 'ts-retrofit3';

import type { IExtra, IResponse } from '@/Module/Core/Model/ApiModel';
import type { IEntity } from '@/Module/Core/Model/DataModel';
import { HttpService } from '@/Module/Core/Service';
import { LESSON_API } from '@/Module/Lesson/Constant';
import type {
  IGetBookMarkLessonById,
  IGetLessonListByCourseIdQuery,
  IGetLikeByEntityIdAndUserId,
  IGetPooyanPlayerTokenResponse,
  IGetRelatedLessonListById,
  IGetTeacherByLessonId,
  ILessonVideoLogs,
  IPostBookMarkLessonById,
  IPostLikeLessonById,
} from '@/Module/Lesson/Model/ApiModel';
import type { IBookmark, ICourse, ICourseCategory, ILessonSection, ILike } from '@/Module/Lesson/Model/DataModel';
import type { ITeacher } from '@/Module/Teacher/Model/DataModel';

class LessonService extends BaseService {
  @GET(LESSON_API.GET_LIST_BY_COURSE_ID)
  async getListByCourseId(
    @QueryMap _query: IGetLessonListByCourseIdQuery,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ILessonSection[]>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_BY_ID)
  async getById(@Path('id') _id: ILessonSection['id'], @Signal _signal?: AbortSignal): Promise<IResponse<ILessonSection>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_BY_FRIENDLY_TOKEN)
  async getByFriendlyToken(
    @Query('friendlyToken') _friendlyToken: ILessonSection['friendlyToken'],
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ILessonSection>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_RELATED_LESSON_LIST_BY_ID)
  async getRelatedLessonListById(
    @QueryMap _query: IGetRelatedLessonListById,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ILessonSection[]>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_LIKE_BY_ENTITY_ID_USER_ID)
  async getLikeByEntityIdAndUserId(
    @QueryMap _query: IGetLikeByEntityIdAndUserId,
    @ExtraMap _extra: IExtra,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ILike>> {
    return <IResponse>{};
  }

  @POST(LESSON_API.POST_LIKE_BY_ID)
  async postLikeById(@Body _body: IPostLikeLessonById, @Signal _signal?: AbortSignal): Promise<IResponse<ILike>> {
    return <IResponse>{};
  }

  @DELETE(LESSON_API.DELETE_LIKE_BY_ID)
  async deleteLikeById(
    @Body _query: Pick<ILike, 'id'>,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<IPostLikeLessonById>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_BOOKMARK_BY_ID)
  async getBookMarkById(
    @QueryMap _query: IGetBookMarkLessonById,
    @ExtraMap _extra: IExtra,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<IBookmark>> {
    return <IResponse>{};
  }

  @POST(LESSON_API.POST_BOOKMARK_BY_ID)
  async postBookMarkById(
    @Body _query: IPostBookMarkLessonById,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<IBookmark>> {
    return <IResponse>{};
  }

  @DELETE(LESSON_API.DELETE_BOOKMARK_BY_ID)
  async deleteBookMarkById(
    @Body _query: Pick<IBookmark, 'id'>,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<IBookmark>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_COURSE_BY_LESSON_ID)
  async getCourseByLessonId(
    @Query('id') _lessonId: ILessonSection['id'],
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ICourse>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_TEACHER_BY_LESSON_ID)
  async getTeacherByLessonId(
    @QueryMap _query: IGetTeacherByLessonId,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ITeacher[]>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_COURSE_CATEGORY_BY_COURSE_ID)
  async getCourseCategoryByCourseId(
    @Query('id') _courseId: ICourse['id'],
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ICourseCategory[]>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.GET_POOYAN_PLAYER_TOKEN)
  async getPooyanPlayerToken(
    @Query('contentId') _contentId: IEntity['id'],
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<{ data: IGetPooyanPlayerTokenResponse }>> {
    return <IResponse>{};
  }

  @GET(LESSON_API.LESSON_VIDEO_STATISTICS)
  async getLessonVideoLogs(
    @Query('lesson_video_id') _videoId: ILessonSection['lessonVideoId'],
    @Header('Authorization') _authorization: string,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse<ILessonVideoLogs>> {
    return <IResponse>{};
  }

  @POST(LESSON_API.LESSON_VIDEO_STATISTICS)
  async sendLessonVideoLogs(
    @Body _body: ILessonVideoLogs,
    @ExtraMap _extra: IExtra,
    @Signal _signal?: AbortSignal,
  ): Promise<IResponse> {
    return <IResponse>{};
  }
}

export default HttpService.build(LessonService);
